import { useState, useEffect } from "react";
import { TagFilter } from "../types";

interface UseFilterPanelOptions {
  filters: TagFilter[];
  loadMetadataValuesForKey?: (key: string) => Promise<void>;
  onAddFilter: (filter: TagFilter) => void;
  onRemoveFilter: (key: string) => void;
  onUpdateFilter: (key: string, filter: Partial<TagFilter>) => void;
}

/**
 * Custom hook that manages filter panel state and logic.
 * Extracts all business logic from the FilterPanel component.
 */
export function useFilterPanel({
  filters,
  loadMetadataValuesForKey,
  onAddFilter,
  onRemoveFilter,
  onUpdateFilter,
}: UseFilterPanelOptions) {
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [selectedValues, setSelectedValues] = useState<Record<string, Set<string>>>({});

  // Initialize selectedValues from existing filters
  useEffect(() => {
    const initialSelected = filters.reduce((acc, filter) => {
      acc[filter.key] = new Set(filter.values);
      return acc;
    }, {} as Record<string, Set<string>>);
    setSelectedValues(initialSelected);
  }, [filters]);

  const handleToggleKey = async (key: string) => {
    if (filters.some((f) => f.key === key)) {
      // Toggle existing filter dropdown
      setExpandedKey(expandedKey === key ? null : key);
      // Re-fetch values against the current filters when opening
      if (expandedKey !== key && loadMetadataValuesForKey) {
        await loadMetadataValuesForKey(key);
      }
    } else {
      // Open dropdown for new filter (don't add filter yet)
      setExpandedKey(key);
      setSelectedValues((prev) => ({ ...prev, [key]: new Set() }));
      // Re-fetch values against the current filters when opening
      if (loadMetadataValuesForKey) {
        await loadMetadataValuesForKey(key);
      }
    }
  };

  const handleSelectValue = (filterKey: string, value: string) => {
    const current = selectedValues[filterKey] || new Set();
    const newSet = new Set(current);

    if (newSet.has(value)) {
      newSet.delete(value);
    } else {
      newSet.add(value);
    }

    // Only update local state, don't trigger filter update yet
    setSelectedValues((prev) => ({ ...prev, [filterKey]: newSet }));
  };

  const handleApplyFilter = (filterKey: string) => {
    const values = Array.from(selectedValues[filterKey] || []);
    if (values.length > 0) {
      // Only add/update filter if values are selected
      const existingFilter = filters.find((f) => f.key === filterKey);
      if (existingFilter) {
        onUpdateFilter(filterKey, { values });
      } else {
        onAddFilter({ key: filterKey, values });
      }
      setExpandedKey(null);
    } else {
      // If no values selected, just close the dropdown without adding filter
      setExpandedKey(null);
      // Remove from selectedValues if it exists
      setSelectedValues((prev) => {
        const newSelected = { ...prev };
        delete newSelected[filterKey];
        return newSelected;
      });
    }
  };

  const handleCancelFilter = (filterKey: string) => {
    // Close dropdown without applying changes
    setExpandedKey(null);
    // Reset selectedValues to match current filter (if exists)
    const existingFilter = filters.find((f) => f.key === filterKey);
    if (existingFilter) {
      setSelectedValues((prev) => ({
        ...prev,
        [filterKey]: new Set(existingFilter.values),
      }));
    } else {
      // Remove from selectedValues if filter doesn't exist
      setSelectedValues((prev) => {
        const newSelected = { ...prev };
        delete newSelected[filterKey];
        return newSelected;
      });
    }
  };

  const handleRemoveFilter = (key: string) => {
    onRemoveFilter(key);
    setExpandedKey(null);
  };

  return {
    expandedKey,
    selectedValues,
    handleToggleKey,
    handleSelectValue,
    handleApplyFilter,
    handleCancelFilter,
    handleRemoveFilter,
  };
}
